import React, { useContext, useEffect, useState } from 'react';
import { Form, Input, InputNumber, Button, Card, Space, message, Modal, Radio, Divider, Select, Checkbox } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { WorkOrderLineItem, WorkOrderLinePart } from '../../types';
import { LaborRatePicker } from '../LaborRates/LaborRatePicker';
import { WatchedMarkupPicker } from '../PartsMarkup/MarkupPicker';
import { AuthContext } from '../../contexts/AuthContext';
import { dollarsToCents } from '../../services/moneyApi';
import { computeDiscount, DiscountMode } from '../../utils/discountMath';
import { formatCurrency } from '../../utils/formatCurrency';
import DiscountToggle from '../Shared/DiscountToggle';
import { useWorkOrderLineSave } from '../../hooks/useWorkOrderLineSave';
import { usePriceChangeGuard, ReapprovalChoice } from '../../hooks/usePriceChangeGuard';
import { getUrgencyLevels, UrgencyLevel } from '../../services/diagnosisListsApi';
import LineItemPhotosControl from '../Shared/LineItemPhotosControl';
import TechnicianTimeTracker from '../Shared/TechnicianTimeTracker';
import { logger } from '../../utils/logger';

const { TextArea } = Input;
const { Option } = Select;

/** Guards LaborRatePicker/WatchedMarkupPicker — both fetch their option list in
 *  a bare useEffect with no internal error boundary. A transient fetch failure
 *  (or, in tests, an api mock that only stubs put/post/patch) would otherwise
 *  crash the whole editor. Degrades to a small inline notice instead. */
class PickerErrorBoundary extends React.Component<
  { children: React.ReactNode; fallback: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; fallback: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error: Error, info: React.ErrorInfo) {
    logger.error('WorkOrderLineInlineEditor picker crashed', error, { info });
  }
  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

export interface WorkOrderLineInlineEditorProps {
  workOrderId: number;
  lineItem: WorkOrderLineItem;
  isNew: boolean;
  isOwner: boolean;
  isLocked?: boolean;
  onSaved: (saved: WorkOrderLineItem) => void;
  onCancel: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  /** Other lines on this work order — used to populate the Details expander's
   *  "Link to Diagnosis" picker (filtered to is_diagnosis items, excluding
   *  this line itself). Optional; defaults to none, which just leaves that
   *  picker empty/disabled. */
  existingLineItems?: WorkOrderLineItem[];
}

/** Labor discount control — Form-aware wrapper around the shared DiscountToggle.
 *  Mirrors ServiceItemsTable's LaborDiscountControl so estimate + WO share the
 *  same math/UI. Values live in `form` under labor_discount_percentage/_amount
 *  (persisted) + labor_discount_final_price (UI-only, no such WO column). */
function LaborDiscountControl({
  form, mode, setMode, disabled, markDirty,
}: {
  form: any;
  mode: DiscountMode;
  setMode: (m: DiscountMode) => void;
  disabled?: boolean;
  markDirty: () => void;
}): React.ReactElement {
  const hours = Form.useWatch('labor_hours', form) || 0;
  const rate = Form.useWatch('labor_rate', form) || 0;
  const percentage = Form.useWatch('labor_discount_percentage', form);
  const amount = Form.useWatch('labor_discount_amount', form);
  const finalPrice = Form.useWatch('labor_discount_final_price', form);
  const base = hours * rate;
  return (
    <DiscountToggle
      base={base}
      round={false}
      disabled={disabled}
      value={{ mode, percentage, amount, finalPrice }}
      onChange={(v) => {
        setMode(v.mode);
        form.setFieldsValue({
          labor_discount_percentage: v.percentage,
          labor_discount_amount: v.amount,
          labor_discount_final_price: v.finalPrice,
        });
        markDirty();
      }}
    />
  );
}

/** Part discount control — same wrapper pattern, rounded to cents (parts persist
 *  rounded money). `base` is live unit_price × quantity for the Net preview. */
function PartDiscountControl({
  partForm, mode, setMode, disabled, markDirty, recompute,
}: {
  partForm: any;
  mode: DiscountMode;
  setMode: (m: DiscountMode) => void;
  disabled?: boolean;
  markDirty: () => void;
  /** Fires the parent PartRow's recompute — needed because Form.setFieldsValue
   *  (used below to write the three discount fields) does NOT trigger the
   *  Form's onValuesChange, which is otherwise the only thing that folds a
   *  discount edit into the saved parts array. `modeOverride` lets us pass the
   *  just-selected mode explicitly, since the `mode` state prop above won't
   *  reflect setMode() until the next render. */
  recompute: (allValues: any, modeOverride?: DiscountMode) => void;
}): React.ReactElement {
  const unitPrice = Form.useWatch('unit_price', partForm) || 0;
  const quantity = Form.useWatch('quantity', partForm) || 0;
  const percentage = Form.useWatch('discount_percentage', partForm);
  const amount = Form.useWatch('discount_amount', partForm);
  const finalPrice = Form.useWatch('discount_final_price', partForm);
  const base = unitPrice * quantity;
  return (
    <DiscountToggle
      base={base}
      round
      disabled={disabled}
      value={{ mode, percentage, amount, finalPrice }}
      onChange={(v) => {
        setMode(v.mode);
        partForm.setFieldsValue({
          discount_percentage: v.percentage,
          discount_amount: v.amount,
          discount_final_price: v.finalPrice,
        });
        markDirty();
        recompute(partForm.getFieldsValue(), v.mode);
      }}
    />
  );
}

interface PartRowProps {
  part: WorkOrderLinePart;
  index: number;
  isOwner: boolean;
  disabled?: boolean;
  markDirty: () => void;
  onChange: (index: number, updated: WorkOrderLinePart) => void;
  onRemove: (index: number) => void;
}

/** One part, expandable in-card: collapsed shows name + total; expanded exposes
 *  part #, qty, description, cost (owner-only), markup picker, unit price, and
 *  the part-level DiscountToggle. Owns its own Form so WatchedMarkupPicker's
 *  Form.useWatch (hardcoded field names) works per-row. */
const PartRow: React.FC<PartRowProps> = ({ part, index, isOwner, disabled, markDirty, onChange, onRemove }) => {
  const [partForm] = Form.useForm();
  const [expanded, setExpanded] = useState(false);
  const [discountMode, setDiscountMode] = useState<DiscountMode>(
    part.discount_amount ? 'amount' : 'percentage'
  );

  const recompute = (allValues: any, modeOverride?: DiscountMode) => {
    const effectiveMode = modeOverride ?? discountMode;
    const quantity = allValues.quantity ?? 1;
    const unitPrice = allValues.unit_price ?? 0;
    const base = quantity * unitPrice;
    const { discountAmount, net } = computeDiscount({
      base,
      mode: effectiveMode,
      percentage: allValues.discount_percentage,
      amount: allValues.discount_amount,
      finalPrice: allValues.discount_final_price,
      round: true,
    });
    const costCentsDollars = allValues.cost_cents_dollars;
    const updated: WorkOrderLinePart = {
      ...part,
      part_number: allValues.part_number || undefined,
      part_name: allValues.part_name,
      quantity,
      unit_price: unitPrice,
      discount_percentage: effectiveMode === 'percentage' ? (allValues.discount_percentage || 0) : 0,
      discount_amount: discountAmount,
      discounted_price: Number((quantity > 0 ? net / quantity : net).toFixed(2)),
      total_price: net,
      markup_preset_id: allValues.markup_preset_id ?? null,
      markup_percentage: allValues.markup_percentage ?? null,
      cost_cents: isOwner
        ? (costCentsDollars != null && costCentsDollars !== '' ? dollarsToCents(costCentsDollars) : null)
        : part.cost_cents ?? null,
      cost_source: isOwner && costCentsDollars != null && costCentsDollars !== ''
        ? 'manual'
        : part.cost_source ?? null,
    };
    onChange(index, updated);
  };

  return (
    <Card size="small" style={{ marginBottom: 8, backgroundColor: '#fafafa' }}>
      <Form
        form={partForm}
        layout="vertical"
        initialValues={{
          part_number: part.part_number,
          part_name: part.part_name,
          quantity: part.quantity ?? 1,
          unit_price: part.unit_price ?? 0,
          markup_preset_id: part.markup_preset_id ?? null,
          markup_percentage: part.markup_percentage ?? null,
          discount_percentage: part.discount_percentage || 0,
          discount_amount: part.discount_amount || 0,
          discount_final_price: null,
          cost_cents_dollars: part.cost_cents != null ? part.cost_cents / 100 : undefined,
        }}
        onValuesChange={(_changed, allValues) => {
          markDirty();
          recompute(allValues);
        }}
      >
        <div
          onClick={() => setExpanded((e) => !e)}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
        >
          <span style={{ fontWeight: 600 }}>{part.part_name || `Part ${index + 1}`}</span>
          <Space>
            <span style={{ color: '#10b981', fontWeight: 600 }}>{formatCurrency(part.total_price)}</span>
            <Button
              size="small"
              danger
              type="text"
              icon={<DeleteOutlined />}
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();
                onRemove(index);
              }}
            />
          </Space>
        </div>

        {expanded && (
          <div style={{ marginTop: 12 }}>
            <div className="grid grid-cols-1 sm:grid-cols-2" style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
              <Form.Item name="part_number" label="Part #" style={{ marginBottom: 8 }}>
                <Input disabled={disabled} placeholder="Part number" />
              </Form.Item>
              <Form.Item name="quantity" label="Qty" style={{ marginBottom: 8 }}>
                <InputNumber min={1} style={{ width: '100%' }} disabled={disabled} />
              </Form.Item>
            </div>

            <Form.Item name="part_name" label="Description" style={{ marginBottom: 8 }}>
              <Input disabled={disabled} placeholder="Part description" />
            </Form.Item>

            {isOwner && (
              <Form.Item
                name="cost_cents_dollars"
                label="Part Cost ($/unit, owner)"
                tooltip="Your cost for this part. Used for profit calculation. Owner-only."
                style={{ marginBottom: 8 }}
              >
                <InputNumber
                  data-testid="part-cost-input"
                  prefix="$"
                  min={0}
                  step={0.01}
                  precision={2}
                  style={{ width: '100%' }}
                  disabled={disabled}
                  placeholder="0.00"
                />
              </Form.Item>
            )}

            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Markup</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Form.Item name="markup_preset_id" hidden><InputNumber /></Form.Item>
                <PickerErrorBoundary fallback={<span style={{ fontSize: 12, color: '#9ca3af' }}>Markup presets unavailable</span>}>
                  <WatchedMarkupPicker
                    form={partForm}
                    disabled={disabled}
                    onPick={(p) => {
                      partForm.setFieldsValue({ markup_preset_id: p.id, markup_percentage: p.markup_percentage });
                      markDirty();
                      recompute(partForm.getFieldsValue());
                    }}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                </PickerErrorBoundary>
                <Form.Item name="markup_percentage" style={{ margin: 0, width: 90, flexShrink: 0 }}>
                  <InputNumber min={0} max={1000} step={1} suffix="%" disabled={disabled} style={{ width: '100%' }} />
                </Form.Item>
              </div>
            </div>

            <Form.Item name="unit_price" label="Unit Price" style={{ marginBottom: 8 }}>
              <InputNumber min={0} precision={2} prefix="$" style={{ width: '100%' }} disabled={disabled} />
            </Form.Item>

            <Form.Item name="discount_percentage" hidden><InputNumber /></Form.Item>
            <Form.Item name="discount_amount" hidden><InputNumber /></Form.Item>
            <Form.Item name="discount_final_price" hidden><InputNumber /></Form.Item>
            <div style={{ marginBottom: 4 }}>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Discount</div>
              <PartDiscountControl
                partForm={partForm}
                mode={discountMode}
                setMode={setDiscountMode}
                disabled={disabled}
                markDirty={markDirty}
                recompute={recompute}
              />
            </div>
          </div>
        )}
      </Form>
    </Card>
  );
};

/** Task 7 — primary-fields + inline-parts + discount-toggle editor for a single
 *  WO line item. Renders Correction + labor (hours/rate/LaborRatePicker/discount)
 *  and the inline part list; each part expands in-card. The 3 C's Details
 *  expander, status/urgency/diagnosis, photos, and time-tracker are Task 10.
 *
 *  useWorkOrderLineSave is a PURE save — it does not compute totals/defaults —
 *  so this editor builds the complete materialized WorkOrderLineItem (labor +
 *  parts totals, discount fields, parts array) before calling saveLine.
 *  usePriceChangeGuard is transport-only: this editor owns the re-approval
 *  choice modal + non-empty notes/staffName + toasts, and — unlike the old
 *  Drawer form's pre-existing bug — calls onSaved exactly once per save. */
const WorkOrderLineInlineEditor: React.FC<WorkOrderLineInlineEditorProps> = ({
  workOrderId,
  lineItem,
  isNew,
  isOwner,
  isLocked,
  onSaved,
  onCancel,
  onDirtyChange,
  existingLineItems = [],
}) => {
  const [form] = Form.useForm();
  // Read the context directly (not useAuth) so this editor degrades gracefully
  // when rendered outside an AuthProvider (mirrors useRelayEmail.ts's pattern).
  const user = useContext(AuthContext)?.user ?? null;
  const { saveLine, saving } = useWorkOrderLineSave();
  const { needsReapproval, resolve } = usePriceChangeGuard();

  const [parts, setParts] = useState<WorkOrderLinePart[]>(lineItem.parts || []);
  const [laborDiscountMode, setLaborDiscountMode] = useState<DiscountMode>(
    lineItem.labor_discount_amount ? 'amount' : 'percentage'
  );
  const [dirty, setDirty] = useState(false);
  const [localSaving, setLocalSaving] = useState(false);

  // Task 10: Details expander — Concern/Cause/status/urgency/diagnosis stay
  // collapsed by default (Correction is the primary always-visible field).
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [urgencyLevels, setUrgencyLevels] = useState<UrgencyLevel[]>([]);
  const isDiagnosisWatched = Form.useWatch('is_diagnosis', form);
  const diagnosisOptions = existingLineItems.filter((item) => item.is_diagnosis && item.id !== lineItem.id);
  const technicianId = user?.id;

  useEffect(() => {
    let cancelled = false;
    getUrgencyLevels()
      .then((levels) => {
        if (!cancelled) setUrgencyLevels(levels.filter((l) => l.is_active));
      })
      .catch((error) => {
        logger.error('Failed to load urgency levels', error as Error, {});
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const [priceChangeVisible, setPriceChangeVisible] = useState(false);
  const [priceChangeData, setPriceChangeData] = useState<{
    oldTotal: number;
    newTotal: number;
    materializedLine: WorkOrderLineItem;
  } | null>(null);
  const [priceChangeChoice, setPriceChangeChoice] = useState<'resend' | 'in_person' | 'proceed'>('resend');

  const markDirty = () => {
    if (!dirty) {
      setDirty(true);
      onDirtyChange?.(true);
    }
  };

  // Task 9: release the dirty flag on cancel, so a parent-owned WebSocket
  // edit-guard (OptimizedWorkOrderDetail's startEditing/stopEditing) doesn't
  // stay latched after the user backs out of an in-progress edit.
  const handleCancel = () => {
    setDirty(false);
    onDirtyChange?.(false);
    onCancel();
  };

  const addPart = () => {
    setParts((prev) => [
      ...prev,
      {
        line_item_id: lineItem.id || 0,
        part_name: '',
        quantity: 1,
        unit_price: 0,
        total_price: 0,
        status: 'needed',
        discount_percentage: 0,
        discount_amount: 0,
        cost: null,
        cost_cents: null,
        markup_preset_id: null,
        markup_percentage: null,
      },
    ]);
    markDirty();
  };

  const updatePart = (index: number, updated: WorkOrderLinePart) => {
    setParts((prev) => prev.map((p, i) => (i === index ? updated : p)));
  };

  const removePart = (index: number) => {
    setParts((prev) => prev.filter((_, i) => i !== index));
    markDirty();
  };

  const materialize = (): WorkOrderLineItem => {
    const values = form.getFieldsValue();
    const laborBase = (values.labor_hours || 0) * (values.labor_rate || 0);
    const { discountAmount: laborDiscountAmount, net: laborTotal } = computeDiscount({
      base: laborBase,
      mode: laborDiscountMode,
      percentage: values.labor_discount_percentage,
      amount: values.labor_discount_amount,
      finalPrice: values.labor_discount_final_price,
      round: false,
    });
    const partsTotal = parts.reduce((sum, p) => sum + (p.total_price || 0), 0);
    const lineTotal = laborTotal + partsTotal;

    return {
      ...lineItem,
      id: lineItem.id,
      correction: values.correction,
      concern: values.concern,
      cause: values.cause,
      shop_status: values.shop_status,
      customer_approval_status: values.customer_approval_status,
      urgency_level_id: values.urgency_level_id ?? null,
      is_diagnosis: values.is_diagnosis || false,
      linked_diagnosis_id: values.is_diagnosis ? undefined : (values.linked_diagnosis_id ?? undefined),
      labor_hours: values.labor_hours || 0,
      labor_rate: values.labor_rate || 0,
      labor_rate_id: values.labor_rate_id ?? null,
      labor_discount_percentage: laborDiscountMode === 'percentage' ? (values.labor_discount_percentage || 0) : 0,
      labor_discount_amount: laborDiscountAmount,
      labor_total: laborTotal,
      parts_total: partsTotal,
      line_total: lineTotal,
      parts,
    };
  };

  const commitSave = async (materializedLine: WorkOrderLineItem) => {
    setLocalSaving(true);
    try {
      const saved = await saveLine({ workOrderId, lineItem: materializedLine, isNew });
      message.success(isNew ? 'Line item created' : 'Line item updated');
      setDirty(false);
      onDirtyChange?.(false);
      onSaved(saved);
    } catch (error) {
      message.error('Failed to save line item');
      console.error('Error saving WO line item:', error);
    } finally {
      setLocalSaving(false);
    }
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
    } catch (error) {
      return;
    }

    const materializedLine = materialize();
    const oldTotal = lineItem.line_total || 0;
    const newTotal = materializedLine.line_total || 0;

    if (needsReapproval({ lineItem, oldTotal, newTotal })) {
      setPriceChangeData({ oldTotal, newTotal, materializedLine });
      setPriceChangeVisible(true);
      return;
    }

    await commitSave(materializedLine);
  };

  const handlePriceChangeConfirm = async () => {
    if (!priceChangeData || !lineItem.id) return;
    setLocalSaving(true);
    try {
      const { materializedLine, oldTotal, newTotal } = priceChangeData;
      // Save exactly once here — the old Drawer form called onSave() both
      // inside its saveLineItem() helper AND again at the end of this
      // confirm handler (double-onSave bug). We call onSaved() a single
      // time below, after the save + reapproval-resolution both complete.
      const saved = await saveLine({ workOrderId, lineItem: materializedLine, isNew });

      const choice: ReapprovalChoice = priceChangeChoice === 'proceed' ? 'skip' : priceChangeChoice;
      if (choice === 'resend') {
        await resolve(workOrderId, lineItem.id, 'resend');
        message.info('Approval status reset to pending');
      } else if (choice === 'in_person') {
        const staffName = user ? `${user.first_name} ${user.last_name}`.trim() || user.email : 'Staff';
        await resolve(workOrderId, lineItem.id, 'in_person', {
          staffName,
          notes: `Re-approved in-person after price change: $${oldTotal.toFixed(2)} → $${newTotal.toFixed(2)}`,
        });
        message.success('Re-approved in-person');
      } else {
        await resolve(workOrderId, lineItem.id, 'skip');
        message.warning('Price changed without customer re-approval');
      }

      setPriceChangeVisible(false);
      setDirty(false);
      onDirtyChange?.(false);
      onSaved(saved);
    } catch (error) {
      message.error('Failed to process price change');
      console.error('Error processing WO line price change:', error);
    } finally {
      setLocalSaving(false);
    }
  };

  const disabled = !!isLocked;
  const busy = saving || localSaving;

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onValuesChange={markDirty}
        initialValues={{
          correction: lineItem.correction,
          concern: lineItem.concern,
          cause: lineItem.cause,
          shop_status: lineItem.shop_status ?? 'new',
          customer_approval_status: lineItem.customer_approval_status ?? 'pending',
          urgency_level_id: lineItem.urgency_level_id ?? undefined,
          is_diagnosis: lineItem.is_diagnosis || false,
          linked_diagnosis_id: lineItem.linked_diagnosis_id,
          labor_hours: lineItem.labor_hours ?? 0,
          labor_rate: lineItem.labor_rate ?? 0,
          labor_rate_id: lineItem.labor_rate_id ?? null,
          labor_discount_percentage: lineItem.labor_discount_percentage ?? 0,
          labor_discount_amount: lineItem.labor_discount_amount ?? 0,
          labor_discount_final_price: null,
        }}
      >
        <Form.Item name="correction" label="Correction (What we did to fix it)">
          <TextArea rows={3} placeholder="Describe what we did to correct the issue..." disabled={disabled} maxLength={500} showCount />
        </Form.Item>

        <div style={{ marginBottom: 12 }}>
          <Button
            type="link"
            style={{ paddingLeft: 0 }}
            onClick={() => setDetailsOpen((o) => !o)}
          >
            {detailsOpen ? 'Hide Details' : 'Details'}
          </Button>
          <div style={{ display: detailsOpen ? 'block' : 'none' }}>
            <Form.Item name="concern" label="Concern (What the customer reported)">
              <TextArea rows={2} placeholder="Describe what the customer reported..." disabled={disabled} maxLength={500} showCount />
            </Form.Item>

            <Form.Item name="cause" label="Cause (What we found was wrong)">
              <TextArea rows={2} placeholder="Describe what we diagnosed..." disabled={disabled} maxLength={500} showCount />
            </Form.Item>

            <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
              <Form.Item name="shop_status" label="Shop Status" style={{ marginBottom: 8 }}>
                <Select disabled={disabled}>
                  <Option value="new">New</Option>
                  <Option value="in_progress">In Progress</Option>
                  <Option value="waiting_parts">Waiting Parts</Option>
                  <Option value="need_approval">Need Approval</Option>
                  <Option value="completed">Completed</Option>
                </Select>
              </Form.Item>

              <Form.Item name="customer_approval_status" label="Customer Approval" style={{ marginBottom: 8 }}>
                <Select disabled={disabled}>
                  <Option value="pending">Pending - Not yet approved</Option>
                  <Option value="approved">Approved - OK to work (verbal/written)</Option>
                  <Option value="declined">Declined - Customer said no</Option>
                </Select>
              </Form.Item>
            </div>

            <Form.Item
              name="urgency_level_id"
              label="Urgency"
              tooltip="How urgent this work is. Shown to the customer and used to prioritize repairs."
            >
              <Select
                placeholder="Select urgency level..."
                allowClear
                disabled={disabled}
                options={urgencyLevels.map((level) => ({ value: level.id, label: level.label }))}
              />
            </Form.Item>

            <Divider style={{ margin: '12px 0' }} />

            <Form.Item name="is_diagnosis" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox disabled={disabled}>Mark as Diagnosis Line Item</Checkbox>
            </Form.Item>

            {!isDiagnosisWatched && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Form.Item name="linked_diagnosis_id" label="Link to Diagnosis (Optional)" style={{ marginBottom: 0 }}>
                  <Select
                    placeholder="Select diagnosis to auto-waive when this repair is approved..."
                    allowClear
                    disabled={disabled || diagnosisOptions.length === 0}
                  >
                    {diagnosisOptions.map((item) => (
                      <Option key={item.id} value={item.id}>
                        {item.concern} - ${(item.line_total || 0).toFixed(2)}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </>
            )}
          </div>
        </div>

        <Card title="Labor" size="small" style={{ marginBottom: 12 }}>
          <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr 1fr' }}>
            <Form.Item name="labor_hours" label="Labor Hours" style={{ marginBottom: 8 }}>
              <InputNumber min={0} step={0.5} precision={2} style={{ width: '100%' }} disabled={disabled} />
            </Form.Item>

            <Form.Item name="labor_rate_id" hidden><InputNumber /></Form.Item>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 6 }}>Labor Rate ($/hr)</div>
              <PickerErrorBoundary fallback={<span style={{ fontSize: 12, color: '#9ca3af' }}>Rate list unavailable</span>}>
                <LaborRatePicker
                  valueId={Form.useWatch('labor_rate_id', form)}
                  disabled={disabled}
                  onPick={(r) => {
                    form.setFieldsValue({ labor_rate_id: r.id, labor_rate: r.hourly_rate });
                    markDirty();
                  }}
                />
              </PickerErrorBoundary>
            </div>

            <Form.Item name="labor_rate" label="Manual Rate ($/hr)" style={{ marginBottom: 8 }}>
              <InputNumber min={0} step={5} precision={2} prefix="$" style={{ width: '100%' }} disabled={disabled} />
            </Form.Item>
          </div>

          {/* Hidden so materialize()'s form.getFieldsValue() (called with no args,
           *  which rc-field-form restricts to MOUNTED Form.Item fields) actually
           *  returns these — LaborDiscountControl only writes them via
           *  form.setFieldsValue, which does not itself register the field. */}
          <Form.Item name="labor_discount_percentage" hidden><InputNumber /></Form.Item>
          <Form.Item name="labor_discount_amount" hidden><InputNumber /></Form.Item>
          <Form.Item name="labor_discount_final_price" hidden><InputNumber /></Form.Item>

          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>Discount</div>
            <LaborDiscountControl form={form} mode={laborDiscountMode} setMode={setLaborDiscountMode} disabled={disabled} markDirty={markDirty} />
          </div>
        </Card>
      </Form>

      <Card
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>Parts</span>
            <Button type="primary" size="small" icon={<PlusOutlined />} disabled={disabled} onClick={addPart}>
              Add Part
            </Button>
          </div>
        }
        size="small"
        style={{ marginBottom: 12 }}
      >
        {parts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 16, color: '#9ca3af' }}>No parts added yet</div>
        ) : (
          parts.map((part, index) => (
            <PartRow
              key={part.id ?? `new-${index}`}
              part={part}
              index={index}
              isOwner={isOwner}
              disabled={disabled}
              markDirty={markDirty}
              onChange={updatePart}
              onRemove={removePart}
            />
          ))
        )}
      </Card>

      {/* Task 10: per-line finding photos + technician time tracker — only
       *  meaningful once the line item has been saved (has an id). Photos are
       *  wrapped in the same error boundary pattern as the pickers above,
       *  since PhotoStrip's useCapability() throws outside an AuthProvider. */}
      {lineItem.id && (
        <Card title="Photos" size="small" style={{ marginBottom: 12 }}>
          <PickerErrorBoundary fallback={<span style={{ fontSize: 12, color: '#9ca3af' }}>Photos unavailable</span>}>
            <LineItemPhotosControl lineItemId={lineItem.id} phase="finding" />
          </PickerErrorBoundary>
        </Card>
      )}

      {lineItem.id && technicianId && (
        <div style={{ marginBottom: 12 }}>
          <TechnicianTimeTracker workOrderId={workOrderId} lineItemId={lineItem.id} technicianId={technicianId} />
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Button onClick={handleCancel} disabled={busy}>Cancel</Button>
        <Button type="primary" onClick={handleSave} loading={busy} disabled={disabled}>Save</Button>
      </div>

      <Modal
        title="Price Changed on Approved Item"
        open={priceChangeVisible}
        onCancel={() => setPriceChangeVisible(false)}
        onOk={handlePriceChangeConfirm}
        confirmLoading={busy}
        okText="Confirm"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>Previously approved at: <strong>{formatCurrency(priceChangeData?.oldTotal)}</strong></div>
          <div>
            New price: <strong>{formatCurrency(priceChangeData?.newTotal)}</strong>
          </div>
          <Divider style={{ margin: '8px 0' }} />
          <Radio.Group value={priceChangeChoice} onChange={(e) => setPriceChangeChoice(e.target.value)}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <Radio value="resend">
                <div>
                  <strong>Re-send for customer approval</strong>
                  <div style={{ fontSize: 12, color: '#666' }}>Status resets to Pending. Customer gets new link.</div>
                </div>
              </Radio>
              <Radio value="in_person">
                <div>
                  <strong>Approve in-person</strong>
                  <div style={{ fontSize: 12, color: '#666' }}>Staff confirms customer agreed at new price.</div>
                </div>
              </Radio>
              <Radio value="proceed">
                <div>
                  <strong>Proceed without approval</strong>
                  <div style={{ fontSize: 12, color: '#faad14' }}>Warning: No customer consent recorded for price change.</div>
                </div>
              </Radio>
            </div>
          </Radio.Group>
        </div>
      </Modal>
    </div>
  );
};

export default WorkOrderLineInlineEditor;
